'use client';

import { useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { useLoginMutation } from './auth-queries';
import type { LoginCredentials } from './types';
import type { SessionLimitState } from '@/features/session-management';

export type { SessionLimitState };

type LoginPhase = 'idle' | 'submitting' | 'redirecting';

// Module-level flag that survives component remounts caused by query
// invalidation in useLoginMutation.onSuccess. Resets naturally on full
// page load triggered by window.location.href.
let navigatingAway = false;

interface UseLoginFormReturn {
  login: (credentials: LoginCredentials) => Promise<void>;
  isLoading: boolean;
  isRedirecting: boolean;
  error: string | null;
  clearError: () => void;
  sessionLimitState: SessionLimitState | null;
  closeSessionLimitModal: () => void;
}

export function useLoginForm(): UseLoginFormReturn {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl');
  const [error, setError] = useState<string | null>(null);
  const [loginPhase, setLoginPhase] = useState<LoginPhase>(
    navigatingAway ? 'redirecting' : 'idle',
  );
  const [sessionLimitState, setSessionLimitState] = useState<SessionLimitState | null>(null);
  const loginMutation = useLoginMutation();

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setError(null);
      setLoginPhase('submitting');

      try {
        const data = await loginMutation.mutateAsync(credentials);

        if (data.success) {
          navigatingAway = true;
          setLoginPhase('redirecting');
          // Use server redirectUrl if specific (e.g. /billing), callbackUrl if present,
          // or root page which resolves the first permitted route via PermissionProvider
          const target =
            data.redirectUrl && data.redirectUrl !== '/overview'
              ? data.redirectUrl
              : callbackUrl ?? '/';
          window.location.href = target;
        } else if (data.errorCode === 'SESSION_LIMIT_EXCEEDED' && data.sessionLimitData) {
          setSessionLimitState({
            accessToken: data.sessionLimitData.accessToken,
            expiresIn: data.sessionLimitData.expiresIn,
            expiresAt: Date.now() + data.sessionLimitData.expiresIn * 1000,
            activeSessions: data.sessionLimitData.activeSessions,
            rememberMe: credentials.rememberMe ?? false,
          });
          setLoginPhase('idle');
        } else if (data.errorCode === 'ACCESS_DENIED') {
          setLoginPhase('redirecting');
          router.push('/access-denied');
        } else {
          setError(data.error || 'Invalid credentials');
          setLoginPhase('idle');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoginPhase('idle');
      }
    },
    [loginMutation, router, callbackUrl],
  );

  const clearError = useCallback(() => setError(null), []);

  const closeSessionLimitModal = useCallback(() => {
    setSessionLimitState(null);
  }, []);

  return {
    login,
    isLoading: loginPhase !== 'idle',
    isRedirecting: loginPhase === 'redirecting',
    error,
    clearError,
    sessionLimitState,
    closeSessionLimitModal,
  };
}
